<template>
  <div>
    <div
      class="card !border-none !p-1 m-0 !h-[calc(100vh-150px)] overflow-y-auto !bg-transparent"
      style="width: 100%"
      v-loading="loading"
      element-loading-text="Loading..."
      element-loading-spinner="el-icon-loading"
      element-loading-background="rgba(0, 0, 0, 0.8)"
    >
      <h3 class="text-black-950 !font-normal text-sm 2xl:!text-base !m-0 !pb-4">
        Application Settings
      </h3>
      <el-form ref="form">
        <div class="!p-1">
          <p class="text-[13px] xl:text-sm 2xl:text-mds text-black-800 !font-normal !m-0 !mb-1.5">
            Default date field format
          </p>
          <p class="text-xs xl:text-[13px] 2xl:text-sm text-black-500 !font-light !m-0">
            Your selection will be applied to all your new documents and templates.
          </p>
          <div style="display: flex; gap: 16px; align-items: center" class="!mt-4">
            <div>
              <span class="text-xs xl:text-[13px] 2xl:text-sm text-black-500 !font-light">
                Date Format :
              </span>
              <el-select v-model="application_settings.date">
                <el-option
                  v-for="(date, index) in formats"
                  :key="index"
                  :value="date"
                  :label="$moment().format(date)"
                ></el-option>
              </el-select>
            </div>
            <div>
              <span class="text-xs xl:text-[13px] 2xl:text-sm text-black-500 !font-light">
                Time Format :
              </span>
              <el-select v-model="application_settings.time">
                <el-option
                  v-for="(item, index) in [12, 24]"
                  :key="index"
                  :value="item"
                  :label="item + ' - hours'"
                >
                  {{ item }} - hours
                </el-option>
              </el-select>
            </div>
          </div>
        </div>

        <el-divider class="m-0 !my-4 !bg-page-border"></el-divider>

        <div class="!p-1">
          <p class="text-[13px] xl:text-sm 2xl:text-mds text-black-800 !font-normal !m-0 !mb-1.5">
            Email Attachment
          </p>
          <p class="text-xs xl:text-[13px] 2xl:text-sm text-black-500 !font-light !m-0">
            When a document is completed, recipients will receive an automated email with a PDF copy
            of the document attached.
          </p>
          <div class="!mt-4">
            <el-checkbox
              class="text-xs xl:text-[13px] 2xl:text-sm text-black-500 !font-light"
              v-model="application_settings.email_attachement.attach_pdf"
            >
              Attach a PDF of completed document to all recipients
            </el-checkbox>
          </div>
        </div>

        <template v-if="isWorkspaceIsBusiness">
          <el-divider class="m-0 !my-4 !bg-page-border"></el-divider>

          <div class="!p-1">
            <p class="text-[13px] xl:text-sm 2xl:text-mds text-black-800 !font-normal !m-0 !mb-1.5">
              Update entity data
            </p>
            <p class="text-xs xl:text-[13px] 2xl:text-sm text-black-500 !font-light !m-0">
              If you used entity fields on document. You can update the entity data by that document
              data.
            </p>
            <div class="!mt-4">
              <el-radio-group v-model="application_settings.update_entity_data">
                <el-radio :label="0">Update after document completion</el-radio>
                <el-radio :label="1">Update after user finishes the document</el-radio>
                <el-radio :label="2">Never update</el-radio>
              </el-radio-group>
            </div>
          </div>
        </template>
        <el-divider class="m-0 !my-4 !bg-page-border"></el-divider>

        <div class="!p-1">
          <p class="text-[13px] xl:text-sm 2xl:text-mds text-black-800 !font-normal !m-0">
            Document Signature
          </p>

          <div class="mt-4 flex flex-col !gap-2">
            <el-checkbox v-model="application_settings.document_signature.allow_sender_auto_fill">
              Auto-complete sender if all fields filled
            </el-checkbox>
            <el-checkbox
              v-model="application_settings.document_signature.show_document_settings_when_sent"
            >
              Show document expiration settings when sending the document
            </el-checkbox>
            <el-checkbox v-model="application_settings.document_signature.show_mail_box_when_sent">
              Show mail box when sending the document
            </el-checkbox>
          </div>
        </div>

        <el-divider class="m-0 !my-4 !bg-page-border"></el-divider>

        <div class="!p-1">
          <p class="text-[13px] xl:text-sm 2xl:text-mds text-black-800 !font-normal !m-0 !mb-1.5">
            Document Security & Authentication
          </p>
          <div class="!mt-4 flex flex-col !gap-2">
            <el-checkbox v-model="application_settings.secure_e_sign.document_access_with_password">
              Document Access with 'Password' Authentication
            </el-checkbox>
            <el-checkbox v-model="application_settings.secure_e_sign.allow_document_e_sign">
              Enable E-Signature 'OTP' Authentication
            </el-checkbox>
          </div>

          <div class="!mt-3" v-if="application_settings.secure_e_sign.allow_document_e_sign">
            <el-checkbox-group
              class="flex items-center !gap-4"
              v-model="application_settings.secure_e_sign.notify_through"
            >
              <el-checkbox label="Send_Email">Send Email</el-checkbox>
              <el-checkbox label="Send_Sms">Send Sms</el-checkbox>
            </el-checkbox-group>
            <span
              style="color: red"
              v-if="sendError && !this.application_settings.secure_e_sign.notify_through.length"
            >
              Please Select Atleast One Option
            </span>
          </div>
          <div class="py-3 flex gap-4">
            <el-checkbox v-model="application_settings.secure_e_sign.enable_text_signature">
              Enable Text Signature
            </el-checkbox>
            <el-checkbox v-model="application_settings.secure_e_sign.enable_draw_signature">
              Enable Draw Signature
            </el-checkbox>
            <el-checkbox v-model="application_settings.secure_e_sign.enable_upload_signature">
              Enable Upload Signature
            </el-checkbox>
          </div>
        </div>

        <el-divider class="m-0 !my-4 !bg-page-border"></el-divider>

        <div class="!p-1">
          <p class="text-[13px] xl:text-sm 2xl:text-mds text-black-800 !font-normal !m-0 !mb-1.5">
            Document Generation Settings
          </p>

          <div class="!mt-4 flex items-center !gap-4">
            <el-checkbox
              v-model="application_settings.document_generation_settings.add_document_generated_id"
            >
              Add eSignature id
            </el-checkbox>
            <el-checkbox
              v-model="application_settings.document_generation_settings.add_page_number"
            >
              Add page numbers
            </el-checkbox>
            <el-checkbox v-model="application_settings.document_generation_settings.add_page_info">
              Add page information
            </el-checkbox>
          </div>
          <div v-if="application_settings.document_generation_settings.add_page_info" class="mt-1">
            <div>
              <label class="!mr-2">Content:</label>
              <el-radio-group
                v-model="application_settings.document_generation_settings.page_info_type"
              >
                <el-radio label="timestamp">Time Stamp</el-radio>
                <el-radio label="text">Text</el-radio>
              </el-radio-group>
              <el-input
                v-if="application_settings.document_generation_settings.page_info_type == 'text'"
                v-model="application_settings.document_generation_settings.page_info_text"
                placeholder="Enter your text here"
              ></el-input>
            </div>
            <div class="mt-1">
              <label class="!mr-2">Alignment:</label>
              <el-radio-group
                v-model="application_settings.document_generation_settings.page_info_alignment"
              >
                <el-radio label="left">Left</el-radio>
                <el-radio label="center">Center</el-radio>
                <el-radio label="right">Right</el-radio>
              </el-radio-group>
            </div>
          </div>
        </div>

        <p class="error" v-if="getError">
          {{ getError }}
        </p>
        <div class="card-footer !border-none !bg-transparent flex justify-start !mt-4 !pl-0">
          <el-form-item class="mb-0 !p-0">
            <button
              class="bg-transparent border hover:text-brand-500 hover:!border hover:!border-brand-500 text-black-700 !px-5 !py-1.5 !rounded-md !border-black-500 text-xs 2xl:text-mds !mr-2"
              @click="goToDashboard"
            >
              Cancel
            </button>
            <el-button
              style="background: var(--brand-500) !important"
              class="!text-white !px-5 !border-brand-500 !py-2.5 !rounded-md text-xs 2xl:text-mds"
              type="button"
              @click="onSubmit"
            >
              Save Changes
            </el-button>
          </el-form-item>
        </div>
      </el-form>
    </div>
  </div>
</template>
<script>
  import { mapGetters, mapActions } from 'vuex';
  import settings from '@/constants/settings';

  import PermissionsHelper from '@/mixins/permissionsHelper';
  export default {
    name: 'ApplicationSettings',
    data() {
      return {
        loading: false,
        sendError: false,
        application_settings: {
          date: '',
          time: 24,
          email_attachement: {
            attach_pdf: true,
          },
          document_signature: {
            require_a_reason: true,
            allow_recepients: false,
            allow_recepient_to_change: false,
            show_document_settings_when_sent: true,
            show_mail_box_when_sent: true,
            allow_sender_auto_fill: false,
          },
          secure_e_sign: {
            document_access_with_password: true,
            enable_text_signature: true,
            enable_draw_signature: true,
            enable_upload_signature: true,
            allow_document_e_sign: true,
            notify_through: [],
          },

          update_entity_data: 0,
          document_generation_settings: {
            add_document_generated_id: true,
            add_page_number: true,
            add_page_info: false,
            page_info_type: 'timestamp',
            page_info_text: '',
            page_info_alignment: 'left',
          },
        },
        formats: [],
      };
    },
    computed: {
      ...mapGetters('settings', ['getApplicationSettings', 'getUpdateApplicationSettingsStatus']),
      ...mapGetters('errors', ['getError']),
    },
    mixins: [PermissionsHelper],
    async mounted() {
      this.loading = true;
      this.formats = settings.DATE_FORMATS;
      this.$store.commit('errors/reset', null, { root: true });
      await this.fetchApplicationSettings();
      this.setApplicationSettings();
      this.loading = false;
    },
    methods: {
      ...mapActions({
        fetchApplicationSettings: 'settings/fetchApplicationSettings',
        updateApplicationSettings: 'settings/updateApplicationSettings',
      }),
      setApplicationSettings() {
        if (this.getApplicationSettings?.data) {
          Object.assign(this.application_settings, {
            date: this.getApplicationSettings.data.date,
            time: this.getApplicationSettings.data.time,
            email_attachement: {
              attach_pdf: this.getApplicationSettings.data.email_attachement.attach_pdf,
            },
            document_signature: {
              ...this.getApplicationSettings.data.document_signature,
            },
            secure_e_sign: {
              ...this.getApplicationSettings.data.secure_e_sign,
            },
            document_generation_settings: {
              ...this.getApplicationSettings.data.document_generation_settings,
            },
            update_entity_data: this.getApplicationSettings.data.update_entity_data,
          });
        }
      },
      async onSubmit() {
        try {
          this.loading = true;
          if (
            this.application_settings.secure_e_sign.enable_text_signature === false &&
            this.application_settings.secure_e_sign.enable_draw_signature === false &&
            this.application_settings.secure_e_sign.enable_upload_signature === false
          ) {
            this.$message.error('Please enable at least one signature type');
            this.sendError = true;
            this.loading = false;
          } else {
            await this.updateApplicationSettings({
              application_settings: this.application_settings,
            });
            if (this.getUpdateApplicationSettingsStatus) {
              this.successNotify('Settings updated successfully');
              await this.fetchApplicationSettings();
              this.setApplicationSettings();
            } else {
              this.errorNotify('Error in updating settings');
            }
            this.loading = false;
          }
        } catch (err) {
          console.log('onSubmit', err);
        }
      },
      goToDashboard() {
        this.setApplicationSettings();
      },
    },
    beforeDestroy() {
      this.$store.commit('settings/setApplicationSettings', null, {
        root: true,
      });
      this.$store.commit('settings/setUpdateApplicationSettingsStatus', null, {
        root: true,
      });
      this.$store.commit('errors/setError', null, {
        root: true,
      });
    },
  };
</script>
<style lang="scss" scoped>
  .sidemenu::-webkit-scrollbar {
    width: 0.7em;
  }

  .sidemenu::-webkit-scrollbar-thumb {
    -webkit-border-radius: 10px;
    background: #e1e1e1;
    -webkit-box-shadow: inset 0 0 6px rgba(172, 169, 169, 0.9);
  }

  .sidemenu::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px #f5f5f5;
    background-color: #f5f5f5;
  }
</style>
